import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';

const PAYTM_HOST = process.env.REACT_APP_PAYTM_HOST;

const loadPaytmScript = (merchantId) =>
  new Promise((resolve) => {
    if (window.Paytm && window.Paytm.CheckoutJS) {
      resolve(true);
      return;
    }

    if (!merchantId) {
      console.error('Paytm Merchant ID not configured');
      resolve(false);
      return;
    }

    if (!document.querySelector('meta[name="viewport"]')) {
      const viewport = document.createElement('meta');
      viewport.name = 'viewport';
      viewport.content = 'width=device-width, height=device-height, initial-scale=1.0, maximum-scale=1.0';
      document.head.appendChild(viewport);
    }

    const script = document.createElement('script');
    script.type = 'application/javascript';
    script.src = `${PAYTM_HOST}/merchantpgpui/checkoutjs/merchants/${merchantId}.js`;
    script.crossOrigin = 'anonymous';
    script.onload = () => {
      console.log('Paytm script loaded successfully');
      resolve(true);
    };
    script.onerror = (err) => {
      console.error('Failed to load Paytm script', err);
      resolve(false);
    };
    document.head.appendChild(script);
  });

/**
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - txnId: string      — Paytm txnToken (session token from Initiate Transaction API)
 * - orderId: string    — order ID echoed back by Paytm
 * - merchantId: string — Paytm MID; used to load the per-merchant checkout script
 * - amount: number     — transaction amount (validated server-side by Paytm)
 * - onError: (data) => void
 * On success backend redirects to /charger; only errors are handled in-page.
 */
export const PaytmPaymentModule = ({
  open,
  onClose,
  txnId,
  orderId,
  merchantId,
  amount,
  onError,
}) => {
  const [status, setStatus] = useState('loading');
  const invokedRef = useRef(false);

  useEffect(() => {
    if (!open || !txnId || !orderId || !merchantId) return;
    if (invokedRef.current) return;

    invokedRef.current = true;
    setStatus('loading');

    loadPaytmScript(merchantId).then((loaded) => {
      if (!loaded) {
        setStatus('error');
        onError?.({ success: false, error: 'Paytm SDK failed to load' });
        return;
      }

      const config = {
        root: '',
        flow: 'DEFAULT',
        data: {
          orderId,
          token: txnId,
          tokenType: 'TXN_TOKEN',
          amount: amount.toString(),
        },
        handler: {
          notifyMerchant(eventName, data) {
            console.log('Paytm notifyMerchant =>', eventName, data);

            if (eventName === 'BACK_BUTTON_PRESSED' || data?.RESPONSE_CODE !== '01') {
              onError?.({
                success: false,
                error: data?.RESPMSG || 'Payment cancelled or failed',
                gateway: 'paytm',
              });
              onClose?.();
            }
          },
        },
      };

      if (window.Paytm && window.Paytm.CheckoutJS) {
        window.Paytm.CheckoutJS.onLoad(() => {
          setStatus('ready');
          window.Paytm.CheckoutJS.init(config)
            .then(() => {
              console.log('Paytm configuration initialized successfully');
              window.Paytm.CheckoutJS.invoke();
            })
            .catch((err) => {
              console.error('Paytm initialization error =>', err);
              setStatus('error');
              onError?.({ success: false, error: err.message });
              onClose?.();
            });
        });
      } else {
        setStatus('error');
        onError?.({ success: false, error: 'Paytm CheckoutJS not available' });
        onClose?.();
      }
    });

    return () => {
      invokedRef.current = false;
    };
  }, [open, txnId, orderId, merchantId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentOutlinedIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight={500}>
            Paytm checkout
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', top: 12, right: 12 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
          {status === 'error' ? (
            <Typography variant="body2" color="error">
              Failed to load Paytm checkout. Please try again.
            </Typography>
          ) : (
            <>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                {status === 'loading' ? 'Loading Paytm checkout…' : 'Opening payment window…'}
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
